import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ['tab', 'content', 'searchInput'];
  static outlets = ['components--navigation-breadcrumbs'];

  static values = {
    checkFormsOnUnload: Boolean,
    updateBreadcrumbs: Boolean
  }

  initialize() {
    this._boundClickHandler = this.switchTab.bind(this);
    this._boundKeydownHandler = (event) => {
      if (event.key === 'Enter') {
        this.switchTab(event);
      } else if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        this._handleArrowNavigation(event);
      }
    };
  }

  tabTargetConnected(tab) {
    tab.addEventListener('click', this._boundClickHandler);
    tab.addEventListener('keydown', this._boundKeydownHandler);
  }

  tabTargetDisconnected(tab) {
    tab.removeEventListener('click', this._boundClickHandler);
    tab.removeEventListener('keydown', this._boundKeydownHandler);
  }

  switchTab(event) {
    if (this.checkFormsOnUnloadValue) {
      this._checkFormsOnUnload(event.currentTarget);

      return false;
    }

    this._switchTabAfterUnloadCheck(event.target);
  }

  _handleArrowNavigation(event) {
    const navigableTabs = this.tabTargets.filter(tab =>
      !tab.classList.contains('disabled') && !tab.classList.contains('locked')
    );
    const currentIndex = navigableTabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowRight': nextIndex = (currentIndex + 1) % navigableTabs.length; break;
      case 'ArrowLeft':  nextIndex = (currentIndex - 1 + navigableTabs.length) % navigableTabs.length; break;
      case 'Home':       nextIndex = 0; break;
      case 'End':        nextIndex = navigableTabs.length - 1; break;
      default: return;
    }

    event.preventDefault();
    const nextTab = navigableTabs[nextIndex];
    const focusTarget = nextTab.querySelector('[role="tab"]') || nextTab;
    focusTarget.focus();
  }

  _switchTabAfterUnloadCheck(target) {
    if (target.nodeName !== 'LI') {
      target = target.closest('li');
    }

    if (!target) return;

    if (target.classList.contains('disabled')) return;

    this._changeTabStatus();
    this._showTargetContent(target);

    target.classList.add('active');
    const activeTabEl = target.querySelector('[role="tab"]');
    if (activeTabEl) activeTabEl.setAttribute('aria-selected', 'true');

    if (this.updateBreadcrumbsValue) {
      const newBreadcrumbText = target.querySelector('.js-text-header').innerHTML.trim();
      this.componentsNavigationBreadcrumbsOutlet.updateCurrentBreadcrumb(newBreadcrumbText);
    }
  }

  _changeTabStatus() {
    this.tabTargets.forEach((tab) => {
      if (tab.classList.contains('active')) {
        tab.classList.remove('active');
        this._forceTabReload(tab);
        const tabEl = tab.querySelector('[role="tab"]');
        if (tabEl) tabEl.setAttribute('aria-selected', 'false');
      }
    });
  }

  _showTargetContent(tabTarget) {
    let contentId = tabTarget.dataset.contentId;

    this.contentTargets.forEach((content) => {
      if (content.id === contentId) {
        content.classList.remove('hidden');
      } else {
        content.classList.add('hidden')
      }
    });
  }

  _forceTabReload(tab) {
    if (this.hasSearchInputTarget && this.searchInputTarget !== '') {
      // Force tab to reload if accessed again
      let contentId = tab.dataset.contentId;
      let contentEl = document.getElementById(contentId);
      contentEl.dataset.contentForceReload = true;
    }
  }

  _checkFormsOnUnload(targetTab) {
    const activeTab = this.tabTargets.filter(tab => {
      return tab.classList.contains('active');
    })[0];

    if (activeTab.dataset.tabsWarningMessageOnUnload) {
      this._verifyForms(targetTab);
    } else {
      this._switchTabAfterUnloadCheck(targetTab);
    }
  }

  async _verifyForms(targetTab) {
    try {
      await this._verify();

      this._switchTabAfterUnloadCheck(targetTab);
    } catch (e) {
      // no-op
    }
  }

  async _verify() {
    const unloadChecker = this.application
      .getControllerForElementAndIdentifier(document.body, "unload-checker");

    return await unloadChecker.verify();
  }
}
